import { CommonModule } from '@angular/common';
import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { forkJoin } from 'rxjs';
import { ErsGridComponent } from '@rcs/grid';
import { ColDef, GridOptions } from 'ag-grid-enterprise';
import { LeaveService } from '../../services/leave.service';
import { LeaveBalance, LeaveLedgerEntry } from '../../models/leave.model';

/**
 * Balances & Accruals — the employee's live leave balances plus the single
 * ledger that produces them. Balance cards come from the (canonical,
 * ledger-derived) balance endpoint; the timeline grid is the raw ledger so a
 * balance always reconciles to the movements shown. Day figures use the body
 * (Poppins) face, not `.ers-money` (which is currency-only per the design system).
 */
@Component({
  selector: 'ers-balances-accruals',
  standalone: true,
  imports: [CommonModule, ErsGridComponent],
  templateUrl: './balances-accruals-page.component.html',
  styleUrls: ['./balances-accruals-page.component.scss'],
})
export class BalancesAccrualsPageComponent implements OnInit {
  private readonly leaveService = inject(LeaveService);

  readonly loading = signal(true);
  readonly balances = signal<LeaveBalance[]>([]);
  readonly ledger = signal<LeaveLedgerEntry[]>([]);
  readonly selectedTypeId = signal<string>('');

  readonly filteredLedger = computed(() => {
    const t = this.selectedTypeId();
    const rows = this.ledger();
    return t ? rows.filter((r) => r.leaveTypeId === t) : rows;
  });

  columnDefs: ColDef[] = [];
  gridOptions: Partial<GridOptions> = {};

  ngOnInit(): void {
    this.setupGrid();
    forkJoin({
      balances: this.leaveService.getMyLeaveBalance(),
      ledger: this.leaveService.getMyLeaveLedger(),
    }).subscribe(({ balances, ledger }) => {
      this.balances.set(balances ?? []);
      this.ledger.set(ledger ?? []);
      this.loading.set(false);
    });
  }

  selectType(id: string): void {
    this.selectedTypeId.set(this.selectedTypeId() === id ? '' : id);
  }

  /** Days of this type that lapse at the leave-year boundary (types that do
   *  not carry forward lose whatever is still available). */
  projectedLoss(b: LeaveBalance): number {
    if (b.isCarriedForward) return 0;
    return Math.max(0, b.availableBalance);
  }

  private setupGrid(): void {
    const dateFmt = (p: { value?: unknown }): string => {
      const v = p?.value;
      if (!v) return '';
      const d = new Date(v as string);
      return isNaN(d.getTime())
        ? String(v)
        : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: '2-digit' });
    };
    const num = (p: { value?: unknown }): string =>
      p?.value == null || p.value === '' ? '' : Number(p.value).toFixed(2);

    this.columnDefs = [
      { headerName: 'Date', field: 'transactionDate', width: 140, valueFormatter: dateFmt, sort: 'desc' },
      { headerName: 'Movement', field: 'transactionType', width: 170 },
      { headerName: 'Leave Type', field: 'leaveTypeName', flex: 1, minWidth: 150 },
      {
        headerName: 'Change', field: 'quantity', width: 120, type: 'rightAligned', valueFormatter: num,
        cellStyle: (p: { value?: unknown }) => ({
          color: Number(p?.value) < 0 ? 'var(--ers-crit)' : 'var(--ers-good)',
          textAlign: 'right',
        }),
      },
      {
        headerName: 'Balance', field: 'runningBalance', width: 130, type: 'rightAligned', valueFormatter: num,
        cellStyle: { textAlign: 'right', fontWeight: '600' },
      },
    ];

    this.gridOptions = {
      suppressCellFocus: true,
      rowHeight: 40,
      headerHeight: 40,
      pagination: true,
      paginationPageSize: 20,
      onGridReady: (p) => p.api.sizeColumnsToFit(),
    };
  }
}
